import {
  Box,
  ClickAwayListener,
  Paper,
  Popper,
  TextField,
  Typography,
  InputAdornment,
} from '@mui/material'
import { FontSize } from '../../../../../styles/commonStyles'
import { intl } from '../../../../../i18n'
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded'
import { useState } from 'react'
import { colorPalette } from '../../../../style/colorPalette'
import { Button } from '../../../../components/buttons'
import { ALERT_COLORS, AlertThreshold } from '../../gridOptions/alertLogic'

export type { AlertThreshold }

type Props = {
  alertThreshold?: AlertThreshold
  onChangeAlertThreshold: (threshold: AlertThreshold) => void
}

const ThresholdInput = ({
  value,
  onChange,
}: {
  value: number
  onChange: (value: number) => void
}) => (
  <TextField
    type="number"
    size="small"
    value={value}
    onChange={e => {
      const newValue = Number(e.target.value)
      if (newValue >= 0 && newValue <= 999) {
        onChange(newValue)
      }
    }}
    InputProps={{
      endAdornment: <InputAdornment position="end">%</InputAdornment>,
    }}
    sx={{
      width: '80px',
      '& .MuiOutlinedInput-input': {
        padding: '6px 6px',
        textAlign: 'right',
        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
          WebkitAppearance: 'none',
          margin: 0,
        },
        MozAppearance: 'textfield',
      },
    }}
  />
)

const ThresholdRow = ({
  color,
  label,
  value,
  onChange,
}: {
  color: string
  label: string
  value: number
  onChange: (value: number) => void
}) => (
  <Box sx={{ marginBottom: '12px', marginLeft: '8px' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Box
        sx={{
          width: '14px',
          height: '14px',
          backgroundColor: color,
          borderRadius: '2px',
          border: `1px solid ${colorPalette.monotone[3]}`,
        }}
      />
      <Typography sx={{ fontSize: '12px', color: colorPalette.monotone[5] }}>
        {label}
      </Typography>
    </Box>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        marginTop: '6px',
        marginLeft: '22px',
      }}
    >
      <Typography sx={{ fontSize: '12px', color: colorPalette.monotone[4] }}>
        {intl.formatMessage({
          id: 'memberWorkload.status.highlight.threshold.prefix',
        })}
      </Typography>
      <ThresholdInput value={value} onChange={onChange} />
      <Typography sx={{ fontSize: '12px', color: colorPalette.monotone[4] }}>
        {intl.formatMessage({
          id: 'memberWorkload.status.highlight.threshold.suffix',
        })}
      </Typography>
    </Box>
  </Box>
)

const RangeThresholdRow = ({
  color,
  label,
  prefix,
  upperValue,
  lowerValue,
  onUpperChange,
  onLowerChange,
}: {
  color: string
  label: string
  prefix: string
  upperValue: number
  lowerValue: number
  onUpperChange: (value: number) => void
  onLowerChange: (value: number) => void
}) => (
  <Box sx={{ marginBottom: '12px', marginLeft: '8px' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Box
        sx={{
          width: '14px',
          height: '14px',
          backgroundColor: color,
          borderRadius: '2px',
          border: `1px solid ${colorPalette.monotone[3]}`,
        }}
      />
      <Typography sx={{ fontSize: '12px', color: colorPalette.monotone[5] }}>
        {label}
      </Typography>
    </Box>
    <Box sx={{ marginTop: '6px', marginLeft: '22px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Typography sx={{ fontSize: '12px', color: colorPalette.monotone[4] }}>
          {prefix}
        </Typography>
        <ThresholdInput value={upperValue} onChange={onUpperChange} />
        <Typography sx={{ fontSize: '12px', color: colorPalette.monotone[4] }}>
          {intl.formatMessage({
            id: 'memberWorkload.status.highlight.upper.suffix',
          })}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          marginTop: '4px',
        }}
      >
        <Typography sx={{ fontSize: '12px', color: colorPalette.monotone[4] }}>
          {intl.formatMessage({
            id: 'memberWorkload.status.highlight.lower.prefix',
          })}
        </Typography>
        <ThresholdInput value={lowerValue} onChange={onLowerChange} />
        <Typography sx={{ fontSize: '12px', color: colorPalette.monotone[4] }}>
          {intl.formatMessage({
            id: 'memberWorkload.status.highlight.lower.suffix',
          })}
        </Typography>
      </Box>
    </Box>
  </Box>
)

const SectionHeader = ({ label }: { label: string }) => (
  <Typography
    sx={{
      fontSize: '13px',
      fontWeight: 600,
      color: colorPalette.monotone[5],
      marginBottom: '12px',
    }}
  >
    {label}
  </Typography>
)

const DEFAULT_THRESHOLD: AlertThreshold = {
  resourcePlanCautionThreshold: 0,
  resourcePlanWarningThreshold: 0,
  estimatedOverCautionThreshold: 0,
  estimatedOverWarningThreshold: 0,
  estimatedUnderCautionThreshold: 0,
  estimatedUnderWarningThreshold: 0,
  actualOverCautionThreshold: 0,
  actualOverWarningThreshold: 0,
  actualUnderCautionThreshold: 0,
  actualUnderWarningThreshold: 0,
}

const HighlightSettingButton = ({
  alertThreshold,
  onChangeAlertThreshold,
}: Props) => {
  const [open, setOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<Element>()
  const [localThresholds, setLocalThresholds] =
    useState<AlertThreshold>(DEFAULT_THRESHOLD)

  const updateThreshold = (key: keyof AlertThreshold) => (value: number) => {
    setLocalThresholds(prev => ({ ...prev, [key]: value }))
  }

  const syncFromProps = () => {
    if (!alertThreshold) return
    setLocalThresholds(alertThreshold)
  }

  const handleClose = () => {
    setAnchorEl(undefined)
    setOpen(false)
    syncFromProps()
  }

  const handleSave = () => {
    onChangeAlertThreshold(localThresholds)
    setAnchorEl(undefined)
    setOpen(false)
  }

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!alertThreshold) return
    if (open) {
      handleClose()
    } else {
      syncFromProps()
      setAnchorEl(event.currentTarget)
      setOpen(true)
    }
  }

  return (
    <div style={{ minWidth: 'fit-content' }}>
      <ClickAwayListener onClickAway={handleClose}>
        <div>
          <button
            style={{
              background: 'transparent',
              border: 0,
              fontSize: FontSize.MEDIUM,
              fontWeight: 500,
              display: 'flex',
              flexDirection: 'row',
              padding: '5px 10px',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={handleOpen}
          >
            <Typography sx={{ color: colorPalette.monotone[5] }}>
              {intl.formatMessage({
                id: 'memberWorkload.status.highlight.setting',
              })}
            </Typography>
            <NavigateNextRoundedIcon
              sx={{
                marginLeft: '5px',
                color: colorPalette.monotone[4],
                cursor: 'pointer',
                transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
              }}
            />
          </button>
          <Popper
            open={open}
            anchorEl={anchorEl}
            placement="bottom-start"
            sx={{ zIndex: 1300, boxShadow: '0px 4px 16px 0px #7B8CAA80' }}
          >
            <Paper
              sx={{
                padding: '16px',
                minWidth: '340px',
                maxHeight: '70vh',
                overflowY: 'auto',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: colorPalette.monotone[5],
                  marginBottom: '8px',
                }}
              >
                {intl.formatMessage({
                  id: 'memberWorkload.status.highlight.setting.title',
                })}
              </Typography>
              <Typography
                sx={{
                  fontSize: '11px',
                  color: colorPalette.monotone[4],
                  marginBottom: '16px',
                }}
              >
                {intl.formatMessage({
                  id: 'memberWorkload.status.highlight.setting.notice',
                })}
              </Typography>

              <Box sx={{ marginBottom: '20px' }}>
                <SectionHeader
                  label={intl.formatMessage({
                    id: 'memberWorkload.status.resourcePlan',
                  })}
                />
                <ThresholdRow
                  color={ALERT_COLORS.caution}
                  label={intl.formatMessage({
                    id: 'memberWorkload.status.highlight.caution.label',
                  })}
                  value={localThresholds.resourcePlanCautionThreshold}
                  onChange={updateThreshold('resourcePlanCautionThreshold')}
                />
                <ThresholdRow
                  color={ALERT_COLORS.warning}
                  label={intl.formatMessage({
                    id: 'memberWorkload.status.highlight.warning.label',
                  })}
                  value={localThresholds.resourcePlanWarningThreshold}
                  onChange={updateThreshold('resourcePlanWarningThreshold')}
                />
              </Box>

              <Box sx={{ marginBottom: '20px' }}>
                <SectionHeader
                  label={intl.formatMessage({
                    id: 'memberWorkload.status.estimated',
                  })}
                />
                <RangeThresholdRow
                  color={ALERT_COLORS.caution}
                  label={intl.formatMessage({
                    id: 'memberWorkload.status.highlight.caution.label',
                  })}
                  prefix={intl.formatMessage({
                    id: 'memberWorkload.status.highlight.estimated.prefix',
                  })}
                  upperValue={localThresholds.estimatedOverCautionThreshold}
                  lowerValue={localThresholds.estimatedUnderCautionThreshold}
                  onUpperChange={updateThreshold(
                    'estimatedOverCautionThreshold'
                  )}
                  onLowerChange={updateThreshold(
                    'estimatedUnderCautionThreshold'
                  )}
                />
                <RangeThresholdRow
                  color={ALERT_COLORS.warning}
                  label={intl.formatMessage({
                    id: 'memberWorkload.status.highlight.warning.label',
                  })}
                  prefix={intl.formatMessage({
                    id: 'memberWorkload.status.highlight.estimated.prefix',
                  })}
                  upperValue={localThresholds.estimatedOverWarningThreshold}
                  lowerValue={localThresholds.estimatedUnderWarningThreshold}
                  onUpperChange={updateThreshold(
                    'estimatedOverWarningThreshold'
                  )}
                  onLowerChange={updateThreshold(
                    'estimatedUnderWarningThreshold'
                  )}
                />
              </Box>

              <Box sx={{ marginBottom: '20px' }}>
                <SectionHeader
                  label={intl.formatMessage({
                    id: 'memberWorkload.status.actual',
                  })}
                />
                <RangeThresholdRow
                  color={ALERT_COLORS.caution}
                  label={intl.formatMessage({
                    id: 'memberWorkload.status.highlight.caution.label',
                  })}
                  prefix={intl.formatMessage({
                    id: 'memberWorkload.status.highlight.actual.prefix',
                  })}
                  upperValue={localThresholds.actualOverCautionThreshold}
                  lowerValue={localThresholds.actualUnderCautionThreshold}
                  onUpperChange={updateThreshold('actualOverCautionThreshold')}
                  onLowerChange={updateThreshold('actualUnderCautionThreshold')}
                />
                <RangeThresholdRow
                  color={ALERT_COLORS.warning}
                  label={intl.formatMessage({
                    id: 'memberWorkload.status.highlight.warning.label',
                  })}
                  prefix={intl.formatMessage({
                    id: 'memberWorkload.status.highlight.actual.prefix',
                  })}
                  upperValue={localThresholds.actualOverWarningThreshold}
                  lowerValue={localThresholds.actualUnderWarningThreshold}
                  onUpperChange={updateThreshold('actualOverWarningThreshold')}
                  onLowerChange={updateThreshold('actualUnderWarningThreshold')}
                />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '8px',
                }}
              >
                <Button
                  onClick={handleSave}
                  sx={{
                    padding: '6px 16px',
                    fontSize: '13px',
                    color: colorPalette.monotone[0],
                    backgroundColor: colorPalette.skyBlue[7],
                    '&:hover': {
                      backgroundColor: colorPalette.skyBlue[5],
                    },
                  }}
                >
                  {intl.formatMessage({
                    id: 'memberWorkload.status.highlight.setting.apply',
                  })}
                </Button>
              </Box>
            </Paper>
          </Popper>
        </div>
      </ClickAwayListener>
    </div>
  )
}

export default HighlightSettingButton
